import api from './client'
import type { CurrencyCode } from '@/lib/format'

export interface HouseholdDetails {
  id: string
  name: string
  ownerUserId: string
  ownerName: string
  createdAt: string
  userCount: number
  trackedPeopleCount: number
  monthlyBudget?: number | null
  monthlySavingsGoal?: number | null
  currency: CurrencyCode
  /** 4 = April–March (default), 1 = January–December. Optional: the frontend can deploy first. */
  financialYearStartMonth?: 1 | 4
  archivedAt: string | null
  isSample?: boolean
}

export interface HouseholdSummary {
  id: string
  name: string
  role: string
  archivedAt: string | null
  isSample?: boolean
}

/** Backs the household switcher. Unlike fetchHousehold, doesn't need X-Household-Id — it's how
 *  the client discovers which household ids exist to send. */
export async function fetchMyHouseholds(): Promise<HouseholdSummary[]> {
  const { data } = await api.get<HouseholdSummary[]>('/households/mine')
  return data
}

export async function createHousehold(
  name: string,
  currency?: CurrencyCode
): Promise<{ id: string; name: string; role: string }> {
  const { data } = await api.post('/households', { name, currency })
  return data
}

export async function fetchHousehold(): Promise<HouseholdDetails> {
  const { data } = await api.get<HouseholdDetails>('/households/current')
  return data
}

export async function renameHousehold(payload: {
  name?: string
  monthlyBudget?: number | null
  monthlySavingsGoal?: number | null
  currency?: CurrencyCode
  financialYearStartMonth?: 1 | 4
}): Promise<HouseholdDetails> {
  const { data } = await api.patch<HouseholdDetails>('/households/current', payload)
  return data
}

/** Owner-only, irreversible. Erases the current household's financial data and categories but
 *  keeps the household, its members, invites and billing. */
export async function resetHousehold(confirmName: string): Promise<void> {
  await api.post('/households/current/reset', { confirmName })
}

/** Owner-only, and only once the household is archived — archive() first. */
export async function deleteHousehold(householdId: string, confirmName: string): Promise<void> {
  await api.delete(`/households/${householdId}`, { data: { confirmName } })
}

/** Owner-only. Freezes the household fully read-only for every member, owner included. */
export async function archiveHousehold(householdId: string): Promise<HouseholdSummary> {
  const { data } = await api.post<HouseholdSummary>(`/households/${householdId}/archive`)
  return data
}

/** Owner-only. Restores an archived household to normal, editable use — subject to the same
 *  3-active-household cap as creating or joining one. */
export async function unarchiveHousehold(householdId: string): Promise<void> {
  await api.post(`/households/${householdId}/unarchive`)
}

/** Leaves a household on your own — active or archived, doesn't matter. Owner must transfer
 *  ownership first if other active members remain. */
export async function leaveHousehold(householdId: string): Promise<void> {
  await api.post(`/households/${householdId}/leave`)
}
